import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link } from "@tanstack/react-router";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { CardField } from "@/components/RecordCard";
import { type AuditEvent, listAuditEvents } from "@/lib/api";

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

interface AuditFilters {
  action: string;
  resourceType: string;
  resourceId: string;
  actorUserId: string;
  since: string;
  until: string;
}

const EMPTY_FILTERS: AuditFilters = {
  action: "",
  resourceType: "",
  resourceId: "",
  actorUserId: "",
  since: "",
  until: "",
};

const PAGE_SIZE = 50;

function filterCount(filters: AuditFilters): number {
  return Object.values(filters).filter((value) => value.trim()).length;
}

function humanizeAction(action: string): string {
  const text = action.replace(/[._]+/g, " ").replace(/\s+/g, " ").trim();
  if (!text) return action;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

// Resource types this app can link to a detail page. Entries with an id
// segment point at the matching route; entries without one point at the hub
// page that owns the concept. Anything not listed here (HR, ops, platform
// concepts) renders as plain text — never a guessed link.
function resourceHref(resourceType: string, resourceId: string): string | null {
  const id = encodeURIComponent(resourceId);
  switch (resourceType) {
    case "invoice":
      return `/invoices/${id}`;
    case "bill":
      return `/bills/${id}`;
    case "credit_note":
      return `/credit-notes/${id}`;
    case "supplier_credit":
      return `/supplier-credits/${id}`;
    case "prepayment":
      return `/prepayments/${id}`;
    case "accrual_schedule":
      return `/accruals/${id}`;
    case "recurring_journal_template":
      return `/recurring-journals/${id}`;
    case "recurring_invoice_schedule":
      return `/recurring-invoices/${id}`;
    case "recurring_bill_schedule":
      return `/recurring-bills/${id}`;
    case "revenue_schedule":
      return `/revenue-schedules/${id}`;
    case "budget":
      return `/settings/budgets/${id}`;
    case "loan":
      return `/loans/${id}`;
    case "fixed_asset":
      return `/fixed-assets/${id}`;
    case "stock_cost":
      return `/stock-costs/${id}`;
    case "bank_account":
      return `/banking/${id}`;
    case "journal_entry":
      return `/journal/${id}`;
    // The profile's resource id is the CRM contact id it extends.
    case "contact_finance_profile":
    case "crm_contact":
      return `/contacts/${id}`;
    case "chart_account":
    case "chart_of_accounts":
      return "/chart-of-accounts";
    case "fiscal_year":
      return "/settings?tab=books&section=fiscal-years";
    case "bank_statement_import":
    case "bank_transaction":
      return "/banking";
    case "payment_batch":
      return "/banking/batches";
    case "scheduled_payment":
      return "/payments?tab=scheduled";
    case "expense_report":
    case "expense_reimbursement":
      return "/expenses";
    case "bank_rule":
      return "/banking/rules";
    case "document_template":
      return "/settings";
    case "reminder_rule":
      return "/reminders?section=rules";
    default:
      if (resourceType.startsWith("tax_") || resourceType === "tax_profile")
        return "/tax";
      return null;
  }
}

function resourceLink(href: string): {
  to: string;
  search?: Record<string, string>;
} {
  const [to, query] = href.split("?");
  return query
    ? { to, search: Object.fromEntries(new URLSearchParams(query)) }
    : { to };
}

/** Actor display: email when the backend resolved one, else a short id. */
function actorLabel(event: AuditEvent): string {
  if (event.actor_email) return event.actor_email;
  if (event.actor_user_id) return `${event.actor_user_id.slice(0, 8)}…`;
  return "—";
}

export function AuditLogPage() {
  const [draft, setDraft] = useState<AuditFilters>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<AuditFilters>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [events, setEvents] = useState<AuditEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [lastPage, setLastPage] = useState<number | null>(null);

  const run = useCallback((filters: AuditFilters, nextPage: number) => {
    setLoading(true);
    setError(null);
    listAuditEvents({
      ...(filters.action.trim() ? { action: filters.action.trim() } : {}),
      ...(filters.resourceType.trim()
        ? { resource_type: filters.resourceType.trim() }
        : {}),
      ...(filters.resourceId.trim()
        ? { resource_id: filters.resourceId.trim() }
        : {}),
      ...(filters.actorUserId.trim()
        ? { actor_user_id: filters.actorUserId.trim() }
        : {}),
      ...(filters.since
        ? { since: new Date(`${filters.since}T00:00:00Z`).toISOString() }
        : {}),
      ...(filters.until
        ? { until: new Date(`${filters.until}T23:59:59Z`).toISOString() }
        : {}),
      limit: PAGE_SIZE,
      page: nextPage,
    })
      .then((rows) => {
        setEvents(rows);
        setLastPage(rows.length < PAGE_SIZE ? nextPage : null);
      })
      .catch((cause: unknown) => setError(toErrorMessage(cause)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    run(applied, page);
  }, [applied, page, run]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (draft.until && draft.since && draft.until < draft.since) {
      setError("Until date must be on or after the since date.");
      return;
    }
    setLastPage(null);
    setPage(1);
    setApplied(draft);
  };

  const clear = () => {
    setDraft(EMPTY_FILTERS);
    setLastPage(null);
    setPage(1);
    setApplied(EMPTY_FILTERS);
  };

  const hasMore = events !== null && events.length === PAGE_SIZE;
  const appliedCount = filterCount(applied);
  const rows = events ?? [];
  const listAvailable = !(error !== null && events === null);
  const emptyMessage =
    appliedCount > 0
      ? "No audit events match these filters."
      : "No audit events yet. Activity in this organization will appear here.";

  return (
    <div className="grid gap-6">
      <PageHeader
        title="Audit log"
        description="Who changed what in this organization, newest first. Narrow the list by action, resource, actor, or date."
      />

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          <div className="mt-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => run(applied, page)}
              disabled={loading}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      <div className="surface grid gap-3 rounded-2xl p-4">
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            variant="secondary"
            aria-expanded={filtersOpen}
            aria-controls="audit-filters"
            onClick={() => setFiltersOpen((open) => !open)}
          >
            Filters
            {appliedCount > 0 ? ` (${appliedCount})` : ""}
          </Button>
          {appliedCount > 0 ? (
            <Button
              type="button"
              variant="ghost"
              onClick={clear}
              disabled={loading}
            >
              Clear
            </Button>
          ) : null}
          <span
            className="text-xs text-[var(--muted-foreground)]"
            aria-live="polite"
          >
            {appliedCount > 0
              ? `${appliedCount} active ${appliedCount === 1 ? "filter" : "filters"}`
              : "All activity shown."}
          </span>
        </div>

        {filtersOpen ? (
          <form
            id="audit-filters"
            onSubmit={submit}
            className="grid gap-4"
            aria-label="Audit log filters"
          >
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <label
                htmlFor="audit-action"
                className="grid content-start gap-1 text-xs font-medium"
              >
                Action prefix
                <Input
                  id="audit-action"
                  value={draft.action}
                  onChange={(e) =>
                    setDraft({ ...draft, action: e.target.value })
                  }
                  placeholder="invoice."
                  disabled={loading}
                />
                <span className="text-[11px] font-normal text-[var(--muted-foreground)]">
                  Prefix match on the raw action — <code>invoice.</code> finds
                  every invoice action.
                </span>
              </label>
              <label
                htmlFor="audit-resource-type"
                className="grid content-start gap-1 text-xs font-medium"
              >
                Resource type
                <Input
                  id="audit-resource-type"
                  value={draft.resourceType}
                  onChange={(e) =>
                    setDraft({ ...draft, resourceType: e.target.value })
                  }
                  placeholder="invoice"
                  disabled={loading}
                />
                <span className="text-[11px] font-normal text-[var(--muted-foreground)]">
                  Exact API type, e.g. <code>invoice</code>, <code>bill</code>,
                  or <code>journal_entry</code>.
                </span>
              </label>
              <label
                htmlFor="audit-resource-id"
                className="grid content-start gap-1 text-xs font-medium"
              >
                Resource ID
                <Input
                  id="audit-resource-id"
                  value={draft.resourceId}
                  onChange={(e) =>
                    setDraft({ ...draft, resourceId: e.target.value })
                  }
                  placeholder="UUID or key"
                  disabled={loading}
                />
                <span className="text-[11px] font-normal text-[var(--muted-foreground)]">
                  Exact record id (UUID or key). Names are not searchable.
                </span>
              </label>
              <label
                htmlFor="audit-actor"
                className="grid content-start gap-1 text-xs font-medium"
              >
                Actor user ID
                <Input
                  id="audit-actor"
                  value={draft.actorUserId}
                  onChange={(e) =>
                    setDraft({ ...draft, actorUserId: e.target.value })
                  }
                  placeholder="UUID"
                  disabled={loading}
                />
                <span className="text-[11px] font-normal text-[var(--muted-foreground)]">
                  Exact user UUID. Actor emails appear on rows but cannot be
                  searched here.
                </span>
              </label>
              <label
                htmlFor="audit-since"
                className="grid content-start gap-1 text-xs font-medium"
              >
                Since
                <Input
                  id="audit-since"
                  type="date"
                  value={draft.since}
                  onChange={(e) => setDraft({ ...draft, since: e.target.value })}
                  disabled={loading}
                />
                <span className="text-[11px] font-normal text-[var(--muted-foreground)]">
                  From the start of the day (UTC).
                </span>
              </label>
              <label
                htmlFor="audit-until"
                className="grid content-start gap-1 text-xs font-medium"
              >
                Until
                <Input
                  id="audit-until"
                  type="date"
                  value={draft.until}
                  onChange={(e) => setDraft({ ...draft, until: e.target.value })}
                  disabled={loading}
                />
                <span className="text-[11px] font-normal text-[var(--muted-foreground)]">
                  Through the end of the day (UTC).
                </span>
              </label>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="submit" disabled={loading}>
                {loading ? "Running…" : "Apply filters"}
              </Button>
            </div>
          </form>
        ) : null}
      </div>

      {listAvailable ? (
        <>
          <div
            className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block"
            data-testid="audit-log-table"
          >
            <div className="table-scroll">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Occurred</TableHead>
                    <TableHead>Actor</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Resource</TableHead>
                    <TableHead>Request</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && events === null ? (
                    <TableRow>
                      <TableCell colSpan={5}>Loading…</TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5}>{emptyMessage}</TableCell>
                    </TableRow>
                  ) : (
                    rows.map((event) => {
                      const href = resourceHref(
                        event.resource_type,
                        event.resource_id,
                      );
                      const target = href ? resourceLink(href) : null;
                      return (
                        <TableRow key={event.id}>
                          <TableCell className="whitespace-nowrap text-xs">
                            {new Date(event.occurred_at).toLocaleString()}
                          </TableCell>
                          <TableCell
                            className="max-w-45 truncate text-xs"
                            title={
                              event.actor_email ??
                              event.actor_user_id ??
                              undefined
                            }
                          >
                            {actorLabel(event)}
                          </TableCell>
                          <TableCell className="text-xs" title={event.action}>
                            {humanizeAction(event.action)}
                          </TableCell>
                          <TableCell className="text-xs">
                            <span className="text-[var(--muted-foreground)]">
                              {event.resource_type}
                            </span>{" "}
                            {target ? (
                              <Link
                                {...target}
                                className="font-mono break-all text-[var(--action)] underline-offset-4 hover:underline"
                              >
                                {event.resource_id}
                              </Link>
                            ) : (
                              <span className="font-mono break-all">
                                {event.resource_id}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-xs break-all">
                            {event.request_id || "—"}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          <div
            className="stack-list grid gap-3 min-[64rem]:hidden"
            data-testid="audit-log-stack"
          >
            {loading && events === null ? (
              <p className="text-sm text-[var(--muted-foreground)]">
                Loading…
              </p>
            ) : rows.length === 0 ? (
              <p className="text-sm text-[var(--muted-foreground)]">
                {emptyMessage}
              </p>
            ) : (
              rows.map((event) => {
                const href = resourceHref(
                  event.resource_type,
                  event.resource_id,
                );
                const target = href ? resourceLink(href) : null;
                return (
                  <article
                    key={event.id}
                    className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-4 text-sm">
                      <time
                        dateTime={event.occurred_at}
                        className="tabular-nums"
                      >
                        {new Date(event.occurred_at).toLocaleString()}
                      </time>
                      <span
                        className="max-w-45 truncate text-xs text-[var(--muted-foreground)]"
                        title={
                          event.actor_email ?? event.actor_user_id ?? undefined
                        }
                      >
                        {actorLabel(event)}
                      </span>
                    </div>
                    <CardField label="Action">
                      <span title={event.action}>
                        {humanizeAction(event.action)}
                      </span>
                    </CardField>
                    <CardField label="Resource">
                      <span className="text-[var(--muted-foreground)]">
                        {event.resource_type}
                      </span>{" "}
                      {target ? (
                        <Link
                          {...target}
                          className="font-mono break-all text-[var(--action)] underline-offset-4 hover:underline"
                        >
                          {event.resource_id}
                        </Link>
                      ) : (
                        <span className="font-mono break-all">
                          {event.resource_id}
                        </span>
                      )}
                    </CardField>
                  </article>
                );
              })
            )}
          </div>
        </>
      ) : null}

      <div className="flex items-center gap-3 text-sm">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => setPage((prev) => Math.max(1, prev - 1))}
          disabled={loading || page <= 1}
        >
          Previous
        </Button>
        <span className="text-[var(--muted-foreground)]" aria-live="polite">
          Page {page}
          {lastPage !== null ? ` of ${lastPage}` : ""} · {PAGE_SIZE} per page
        </span>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => setPage((prev) => prev + 1)}
          disabled={loading || !hasMore}
          title={
            hasMore
              ? "Load the next page"
              : "Fewer than a full page returned — this is the last page"
          }
        >
          Next
        </Button>
      </div>
    </div>
  );
}
